'use client';

import { Box, CircularProgress, Typography } from '@mui/material';
import React, { FunctionComponent, ReactNode, useMemo } from 'react';
import Header from './Header/Header';
import { useDim } from '../../contexts/DimContext';
import Footer from './Footer/Footer';
import ContactUs from '../contactUs/ContactUs';
import { useLocale } from '../../contexts/LocaleProvider';
import { IntlProvider } from 'react-intl';
import { useLoading } from '../../contexts/LoadingContext';
import PageContainer from '../productPage/tableComponents/PageContainer';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import ReusableTabs from '../common/ReusableTabs';
import ProductHomeImage from '../../static/product_image/productHome.png';
import BreadCrumbs from '../common/BreadCrumbs';
import { useSearchParams } from 'react-router-dom';
import { useCommonContent } from '../../graphql/pimcore/modules/siteContent/hooks/useCommonContent';
import { formatIcuString } from '../../utils/Locale';
import { toUrl } from '@/utils/convertImagePath';

interface LayoutProps {
    children: ReactNode;
}

const Layout: FunctionComponent<LayoutProps> = ({ children }) => {
    const { isDimmed } = useDim();
    const { locale, messages } = useLocale();
    const { contentTree: cmsCommonContent, loading: cmsCommonContentLoading } = useCommonContent();

    const { isLoading: ctxLoading, searchInput, setSearchInput } = useLoading(); // ← 需要 setter
    const [searchParams] = useSearchParams();

    const isLoading = ctxLoading || cmsCommonContentLoading;

    // 1. 讀 search=? 的參數
    const rawKeyword = searchParams.get('search') ?? '';

    // 2. 處理 URL 編碼，並把「+」還原成空白
    const keyword = rawKeyword.replace(/\+/g, ' ');

    const seartchBannerTitle = useMemo(() => {
        const term = searchInput.trim() ? searchInput : keyword;
        return formatIcuString(cmsCommonContent.search.searchingFor.content, { term }, locale);
    }, [cmsCommonContent.search.searchingFor.content, searchInput, keyword, locale]);

    return (
        <>
            <IntlProvider locale={locale} key={locale} defaultLocale="en" messages={messages}>
                <Header />
                <Box display="flex" data-testid={'main-zone'}>
                    {isDimmed && (
                        <Box
                            sx={{
                                position: 'fixed', // ← 關鍵：固定在視窗
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh', // 覆蓋整個可視區
                                bgcolor: 'rgba(73,74,77,0.3)',
                                zIndex: 13,
                                pointerEvents: 'auto',
                            }}
                            data-testid="dim-overlay"
                        />
                    )}
                    {isLoading ? (
                        <Box
                            width="100%"
                            component="main"
                            sx={{
                                padding: 0,
                                flexGrow: 0,
                                p: 0, // 内边距
                            }}
                        >
                            <ReusableTabs
                                bannerImage={toUrl(ProductHomeImage)}
                                bannerTitle={seartchBannerTitle}
                                bannerDescription=""
                                tabLabels={[]}
                                tabKeys={['']}
                                tabContents={[
                                    <PageContainer
                                        key="loading"
                                        containerName={'search'}
                                        sx={{
                                            padding: 0,
                                            margin: 0,
                                            height: 'auto',
                                            backgroundColor: 'white',
                                        }}
                                    >
                                        <Box
                                            key="search-content"
                                            sx={{
                                                backgroundColor: 'white',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                margin: 'auto',
                                                height: '500px',
                                            }}
                                        >
                                            <CircularProgress
                                                size={180}
                                                sx={{
                                                    color: '#E5673E',
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    marginTop: '1rem',
                                                    fontSize: '1.5rem',
                                                    fontWeight: 600,
                                                    fontFamily: 'Poppins',
                                                }}
                                            >
                                                {cmsCommonContent.misc.loadingYourContent.content}
                                            </Typography>
                                        </Box>
                                    </PageContainer>,
                                ]}
                                bannerFontStyle={styles.bannerFont}
                                subBannerFontStyle={styles.bannerFont2}
                                pageName={'search'}
                                showTabs={true}
                                breadcrumbs={<BreadCrumbs navigationPath="/Home" />}
                            />
                        </Box>
                    ) : (
                        children
                    )}
                </Box>
                <ContactUs />
                <Footer />
            </IntlProvider>
        </>
    );
};

export default Layout;
